import { memo, useEffect, useState, useRef } from 'react';
import { UploadProps, Upload } from 'antd';
import AutoSizeImage from './auto-size-image';
import UploadPictureExample from '@/sub-luca-plugin/components/picture-crop/upload-picture-example';
import CombineLoading from './combine-loading';
import { CombineCompressUpload, ICombineCompressUploadRef } from '@/sub-luca-plugin/compatible-components';
import { SortableContainer, SortableElement, SortEndHandler } from 'react-sortable-hoc';
import { previewPicture } from '@/sub-luca-plugin/utils';
import { loadImg } from '@/sub-luca-plugin/utils/tools';
import { uploadLimit } from '@/sub-luca-plugin/utils/qa';
import { eventBusExample } from '@/sub-luca-plugin/utils/class-example';
import { useControlDrawerTransform } from '@/sub-luca-plugin/utils/custom-hooks';
import { INoticeData } from '@/sub-luca-plugin/types';
import { searchQuestionByOcr } from '../qa-render/helper';
import classNames from 'classnames';
import { tCommon } from '@/sub-luca-plugin/i18n';
import './index.scss';

const Picture = SortableElement<{ id: number; url: string }>(({ url, id }: { id: number; url: string }) => {
    return (
        <li className="combine-pictures-drawer__sortable-element">
            <div className="combine-pictures-drawer__picture-delete" id={`delete-${id}`}></div>
            <div className="combine-pictures-drawer__picture-container">
                <AutoSizeImage className="combine-pictures-drawer__picture" src={url} />
            </div>
        </li>
    );
});

const PicturesContainer = SortableContainer<{ urls: string[] }>(({ urls }: { urls: string[] }) => {
    const handleUploadChange: UploadProps['customRequest'] = info => {
        const localUrl = URL.createObjectURL(info.file as File);
        // 跳转
        eventBusExample.publish('PictureCrop', { type: 'crop', params: { url: localUrl } });
    };

    return (
        <ul className="combine-pictures-drawer__sortable-container">
            {urls.map((url, index) => (
                <Picture key={url} id={index} url={url} index={index} />
            ))}
            {urls.length < 6 && (
                <Upload accept=".jpg, .jpeg, .png" showUploadList={false} beforeUpload={uploadLimit} customRequest={handleUploadChange}>
                    <div className="combine-pictures-drawer__upload-picture">{tCommon.t('albumImport', '相册导入')}</div>
                </Upload>
            )}
        </ul>
    );
});

const CombinePicturesDrawer = () => {
    const exampleMaxHeight = useRef('0');
    const initParams = useRef<Record<string, any>>({});
    const isHomeOcr = !!initParams.current.lucaId;
    const combineCompressUploadRef = useRef<ICombineCompressUploadRef>(null);
    const [exampleInfo, setExampleInfo] = useState({ visible: false, title: '' });
    const [pictures, setPictures] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const { style, showDrawer, hideDrawer } = useControlDrawerTransform({ needTransition: !isHomeOcr });

    const revokeObjectURLs = (urls: string[]) => {
        urls.forEach(url => URL.revokeObjectURL(url));
    };

    const handleSearchQuestionsAfterComposite = async () => {
        if (!pictures.length || loading) return;
        setLoading(true);
        try {
            const ocrUrl = await combineCompressUploadRef.current!.getCombinePictureUrl(pictures);
            // 由于合并的图片较大，这里提前加载该资源，防止关闭弹层时，合并的图片显示空白
            const loadImgPromise = loadImg(ocrUrl);
            await searchQuestionByOcr({ ocrUrl });
            loadImgPromise.then(handleCloseDrawer);
        } catch (error) {}
        setLoading(false);
    };

    const handleProjectSubjectSelect = () => {
        if (!pictures.length) return;
        eventBusExample.publish('QaInitOverlay', {
            type: 'initByLucaId',
            params: { ...initParams.current, allowChangeDeterminedIds: true, showProjectSelectCloseBtn: true },
            callbacks: {
                // 开始合并搜索
                startCombineSearch: async (aiAskOnOff: boolean) => {
                    if (aiAskOnOff) {
                        handleSearchQuestionsAfterComposite();
                    } else {
                        setLoading(true);
                        const cdnUrl = await combineCompressUploadRef.current!.getCombinePictureUrl(pictures);
                        eventBusExample.publish('DialogueTool', { type: 'echo', params: { images: [cdnUrl] } });
                        handleCloseDrawer();
                    }
                },
            },
        });
    };

    const handleSortEnd: SortEndHandler = ({ oldIndex, newIndex }) => {
        if (oldIndex === newIndex) {
            previewPicture(pictures[oldIndex]);
            return;
        }
        const newPictures = [...pictures];
        [newPictures[oldIndex], newPictures[newIndex]] = [newPictures[newIndex], newPictures[oldIndex]];
        setPictures(newPictures);
    };

    const handleShouldCancelStart = (ev: any) => {
        const id: string = ev.target.id || '';
        if (id.includes('delete')) {
            setPictures(oldList => oldList.filter((_, index) => index !== +id.split('-')[1]));
            return true;
        }
        return false;
    };

    const handleCloseDrawer = () => {
        initParams.current = {};
        setLoading(false);
        hideDrawer();
        setPictures([]);
        handleCloseExample();
        revokeObjectURLs(pictures);
    };

    const handleShowExample = (title: string) => setExampleInfo({ visible: true, title });

    const handleCloseExample = () => setExampleInfo({ visible: false, title: '' });

    const handleClickCloseIcon = () => {
        if (isHomeOcr) {
            eventBusExample.publish('QaDrawer', { type: 'close' });
            setTimeout(handleCloseDrawer, 500);
        } else {
            handleCloseDrawer();
        }
    };

    useEffect(() => {
        eventBusExample.subscribe('CombinePicturesDrawer', ({ type, params = {} }: INoticeData) => {
            const { url, lucaId, defaultIds } = params;
            switch (type) {
                case 'crop_success':
                    lucaId && (initParams.current = { lucaId, defaultIds });
                    setPictures(oldUrls => oldUrls.concat(url));
                    showDrawer();
                    break;
                default:
                    handleCloseDrawer();
                    break;
            }
        });
        exampleMaxHeight.current = document.body.clientHeight - 48 + 'px';
    }, []);

    return (
        <>
            <CombineCompressUpload ref={combineCompressUploadRef} />
            {loading && <CombineLoading />}
            <div className="combine-pictures-drawer" style={style}>
                <div className="combine-pictures-drawer__header">
                    <div className="combine-pictures-drawer__title">{tCommon.t('snapAnswer', '拍题答疑')}</div>
                    <div
                        className="combine-pictures-drawer__example"
                        onClick={() => handleShowExample(tCommon.t('photoGuidelines', '拍题示例'))}
                    >
                        {tCommon.t('guidelines', '示例')}
                    </div>
                    <div className="combine-pictures-drawer__close" onClick={handleClickCloseIcon} />
                </div>
                <div className="combine-pictures-drawer__drag-tips">{tCommon.t('longPressToSort', '长按可拖动排序')}</div>
                <PicturesContainer axis="xy" urls={pictures} onSortEnd={handleSortEnd} shouldCancelStart={handleShouldCancelStart} />
                <footer className="combine-pictures-drawer__footer">
                    <div
                        className={classNames(
                            'combine-pictures-drawer__merge-btn',
                            !pictures.length && 'combine-pictures-drawer__merge-btn--disabled'
                        )}
                        onClick={() => {
                            isHomeOcr ? handleProjectSubjectSelect() : handleSearchQuestionsAfterComposite();
                        }}
                    >
                        {pictures.length > 1 ? tCommon.t('mergedSearch', '合并搜题') : tCommon.t('searchNow', '立即搜题')}
                    </div>
                </footer>
            </div>
            <UploadPictureExample
                className="combine-pictures-drawer__upload-example"
                maxHeight={exampleMaxHeight.current}
                {...exampleInfo}
                onClose={handleCloseExample}
                showMode="translateY"
            />
        </>
    );
};

export default memo(CombinePicturesDrawer);
